import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  RunShopState,
  ShopOffer,
  describeConsumableEffect,
  describeJokerEffect,
} from '@solante/shared';
import { RARITY_COLORS, RARITY_BADGE_COLORS } from '../../../utils/gameConstants';

interface PracticeShopPanelProps {
  shop: RunShopState;
  chips: number;
  jokerSlots: number;
  currentJokerCount: number;
  purchasedOfferIds: Set<string>;
  onPurchase: (offerId: string) => Promise<void>;
  onReroll: () => Promise<void>;
  onReady: () => Promise<void>;
}

const describeOffer = (offer: ShopOffer) => {
  switch (offer.kind) {
    case 'joker':
      return {
        title: offer.joker.name,
        detail: offer.joker.description,
        cost: offer.joker.cost,
        rarity: offer.joker.rarity,
        effects: offer.joker.effects.map((e) => describeJokerEffect(e)).join(', '),
      };
    case 'consumable':
      return {
        title: offer.consumable.name,
        detail: offer.consumable.description,
        cost: offer.consumable.cost,
        rarity: 'uncommon' as const,
        effects: offer.consumable.effects.map((e) => describeConsumableEffect(e)).join(', '),
      };
    case 'service':
      return {
        title: offer.service.name,
        detail: offer.service.description,
        cost: offer.service.cost,
        rarity: 'rare' as const,
        effects: '',
      };
    default:
      return {
        title: 'Unknown Offer',
        detail: 'Details unavailable',
        cost: 0,
        rarity: 'common' as const,
        effects: '',
      };
  }
};

const OfferCard: React.FC<{
  offer: ShopOffer;
  chips: number;
  canBuyJoker: boolean;
  isSold: boolean;
  onPurchase: () => void;
}> = ({ offer, chips, canBuyJoker, isSold, onPurchase }) => {
  const { title, detail, cost, rarity, effects } = describeOffer(offer);
  const canAfford = chips >= cost;
  const isJoker = offer.kind === 'joker';
  const canBuy = canAfford && !isSold && (isJoker ? canBuyJoker : true);

  const rarityColors = RARITY_COLORS;
  const rarityBadgeColors = RARITY_BADGE_COLORS;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isSold ? 0.5 : 1, y: 0 }}
      className={`relative rounded-xl border p-4 ${rarityColors[rarity]} ${isSold ? 'opacity-50' : ''}`}
    >
      {isSold && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl z-10">
          <span className="text-lg font-bold text-white/60">SOLD</span>
        </div>
      )}

      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-semibold text-white">{title}</h4>
          <span className={`text-xs px-2 py-0.5 rounded ${rarityBadgeColors[rarity]}`}>
            {rarity}
          </span>
        </div>
        <span className="text-xs uppercase text-white/40">{offer.kind}</span>
      </div>

      <p className="text-sm text-white/60 mb-2 line-clamp-2">{detail}</p>
      {effects && <p className="text-xs text-white/40 mb-3">{effects}</p>}

      <div className="flex items-center justify-between">
        <span className="text-yellow-400 font-medium">{cost} chips</span>
        <button
          type="button"
          onClick={onPurchase}
          disabled={!canBuy}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            canBuy
              ? 'bg-purple-600 hover:bg-purple-500 text-white'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
        >
          {isSold
            ? 'Sold'
            : !canAfford
              ? 'Too expensive'
              : isJoker && !canBuyJoker
                ? 'Full'
                : 'Buy'}
        </button>
      </div>
    </motion.div>
  );
};

export const PracticeShopPanel: React.FC<PracticeShopPanelProps> = ({
  shop,
  chips,
  jokerSlots,
  currentJokerCount,
  purchasedOfferIds,
  onPurchase,
  onReroll,
  onReady,
}) => {
  const [pending, setPending] = useState<string | null>(null);
  const canBuyJoker = currentJokerCount < jokerSlots;
  const rerollCost = shop.rerollCost;
  const canReroll = chips >= rerollCost;

  const handlePurchase = async (offerId: string) => {
    if (pending) return;
    setPending(offerId);
    try {
      await onPurchase(offerId);
    } finally {
      setPending(null);
    }
  };

  const handleReroll = async () => {
    if (pending) return;
    setPending('reroll');
    try {
      await onReroll();
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-display text-2xl font-bold text-emerald-400 mb-1">SHOP</h2>
        <p className="text-sm text-white/60">
          Chips: <span className="text-yellow-400 font-medium">{chips}</span>
        </p>
      </div>

      {/* Offers grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shop.offers.map((offer) => (
          <OfferCard
            key={offer.offerId}
            offer={offer}
            chips={chips}
            canBuyJoker={canBuyJoker}
            isSold={purchasedOfferIds.has(offer.offerId)}
            onPurchase={() => handlePurchase(offer.offerId)}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-center">
        <button
          type="button"
          onClick={handleReroll}
          disabled={!canReroll || pending !== null}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            canReroll && !pending
              ? 'bg-amber-600 hover:bg-amber-500 text-white'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
        >
          {pending === 'reroll' ? 'Rerolling...' : `Reroll (${rerollCost} chips)`}
        </button>
        <button
          type="button"
          onClick={onReady}
          className="px-8 py-2 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-500 text-white"
        >
          Ready
        </button>
      </div>

      {/* Info */}
      <p className="text-center text-xs text-white/40">
        Joker slots: {currentJokerCount}/{jokerSlots}
      </p>
    </div>
  );
};
